// Single source of truth for tunable constants. Workers and clients import from here.

import { BRAND } from './brand';

export const RATE_LIMIT = {
  perDevicePerDay: 3,
  perIpPerDay: 10,
  /** The rolling daily window, in seconds. */
  ttlSeconds: 86_400,
  /** The rolling monthly window, in seconds (30 days). */
  monthSeconds: 30 * 86_400
} as const;

/**
 * Anonymous "freemium hook": a guest may generate this many apps before
 * the auth wall asks them to sign in (it stays free — signing in just
 * lifts them onto the PLANS caps and unlocks cross-device sync).
 */
export const GUEST_FREE_GENERATIONS = 2;

/**
 * Founder credentials are NOT compiled into source.
 *
 * The platform's founding account (granted admin powers — bypasses
 * generation ceilings, rate limits, and the guest wall) is identified by
 * a Worker-side secret `FOUNDER_EMAIL`, compared against the verified
 * session email server-side. When the secret is unset, NO email resolves
 * to admin — the bypass fails closed.
 *
 * The dev-recovery OTP that paired with the founder email (formerly
 * `MASTER_OTP = '000000'`) has been removed from the codebase. The
 * gateway now requires `FOUNDER_OTP` as a Worker secret and refuses the
 * bypass when it is unset. Pre-launch hardening — see
 * POMELA_NIGHT_AUDIT_REPORT.md §1.1.
 *
 * Helpers below normalize env reads to a single shape; do not bypass
 * them by reading the raw env directly.
 */
export interface FounderEnv {
  FOUNDER_EMAIL?: string;
  FOUNDER_OTP?: string;
}

/** Lower-cased founder email, or null when no founder is configured. */
export function founderEmail(env: FounderEnv | undefined): string | null {
  const raw = env?.FOUNDER_EMAIL?.trim().toLowerCase() ?? '';
  return raw || null;
}

/** The configured founder OTP, or null when bypass is disabled. */
export function founderOtp(env: FounderEnv | undefined): string | null {
  const raw = env?.FOUNDER_OTP?.trim() ?? '';
  // Must be a 6-digit numeric string to ever match. An accidentally
  // unset env var would otherwise compare equal to an empty submitted
  // code and grant access.
  return /^\d{6}$/.test(raw) ? raw : null;
}

/** True iff `email` is the configured founder. Both sides normalized. */
export function isFounderEmail(env: FounderEnv | undefined, email: string | undefined | null): boolean {
  const f = founderEmail(env);
  if (!f || !email) return false;
  return email.trim().toLowerCase() === f;
}

/** Env shape for the private-preview allowlist (Sprint 66A-RU). */
export interface PreviewEnv extends FounderEnv {
  /** Optional comma-separated list of tester emails admitted during private preview. */
  PREVIEW_ALLOWLIST?: string;
  /**
   * A SEPARATE, ADDITIVE allowlist for synthetic QA identities.
   *
   * It exists because Cloudflare secrets are write-only: `PREVIEW_ALLOWLIST` already holds real
   * private-preview testers, and its value cannot be read back to append to. Overwriting it blind
   * would silently revoke generation access for people who have it. A second variable widens the
   * gate for QA without ever reading, replacing or risking the first.
   *
   * It carries exactly the same authority as `PREVIEW_ALLOWLIST` — the §maintenance gate and nothing
   * else. It grants no admin, no founder status, and no bypass of credits, rate limits or spend caps.
   */
  QA_PREVIEW_ALLOWLIST?: string;
}

/**
 * Parse one comma-separated allowlist into lower-cased, de-duped, exact-match entries.
 *
 * The matching this feeds is EXACT string equality, deliberately. No substring, no domain wildcard,
 * no prefix. `pomela.app` in the list must not admit `evil@pomela.app.attacker.com`, and
 * `qa-verify@pomela.app` must not admit `xqa-verify@pomela.app`.
 */
function parseAllowlist(raw: unknown): string[] {
  // Typed as `unknown`, not `string | undefined`, on purpose. These values arrive from a Workers env
  // binding at runtime, where the compiler's opinion is a hope rather than a guarantee. The first
  // version called `.trim()` directly and threw a TypeError on a non-string — which in a gate means
  // the request fails OPEN or 500s rather than failing closed. Anything that is not a string is
  // treated as no allowlist at all.
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (!s) return [];
  const seen = new Set<string>();
  for (const part of s.split(',')) {
    const e = part.trim().toLowerCase();
    if (e) seen.add(e);
  }
  return [...seen];
}

/**
 * Lower-cased, de-duped tester allowlist from `PREVIEW_ALLOWLIST` (comma-separated).
 * Empty by default — when unset, the private-preview gate stays founder-only.
 */
export function previewAllowlist(env: PreviewEnv | undefined): string[] {
  return parseAllowlist(env?.PREVIEW_ALLOWLIST);
}

/**
 * Lower-cased, de-duped QA allowlist from `QA_PREVIEW_ALLOWLIST` (comma-separated).
 * Empty by default. A missing or malformed value yields an EMPTY list — fail closed.
 */
export function qaPreviewAllowlist(env: PreviewEnv | undefined): string[] {
  return parseAllowlist(env?.QA_PREVIEW_ALLOWLIST);
}

/**
 * True iff `email` may generate during the private preview — the FOUNDER, or an
 * email on the optional `PREVIEW_ALLOWLIST`. Both sides normalized. This widens
 * ONLY the §maintenance gate; it grants NO god-mode — allowlisted testers still
 * pass through every credit / rate-limit / spend-cap protection (only the founder
 * `isFounderEmail`/`isAdmin` bypasses those). With `PREVIEW_ALLOWLIST` unset this
 * is identical to `isFounderEmail` (off-by-default), so the gate never weakens.
 */
export function isPreviewUser(env: PreviewEnv | undefined, email: string | undefined | null): boolean {
  if (!email) return false;
  if (isFounderEmail(env, email)) return true;
  const e = email.trim().toLowerCase();
  // ADDITIVE union. QA_PREVIEW_ALLOWLIST widens this gate and nothing else; when it is unset or
  // malformed its list is empty and behaviour is byte-identical to before it existed, so an existing
  // preview tester's access cannot be changed by anything to do with QA.
  return previewAllowlist(env).includes(e) || qaPreviewAllowlist(env).includes(e);
}

/**
 * A principal's first N generations synthesise on the premium model
 * regardless of plan — the quality hook that turns a trial into a habit.
 */
export const PREMIUM_HOOK_GENERATIONS = 2;

// Hard cap on a user prompt. Enforced at the gateway AND the generation
// worker (defence in depth). See docs/security/audit-2026-05.md finding I-1.
export const MAX_PROMPT_CHARS = 2000;

export const BUNDLE_BUDGET = {
  // §A4 — DATA-DRIVEN, earned back DOWN from the 200K stopgap to 160K.
  // The 200K stopgap (set when ordinary apps clustered at 146-149KB gz against
  // the old 150K ceiling) is NOT the fix. Root cause found + fixed: VEmptyState,
  // VFAB, VTabBar, VSelect + VTextArea — present in NEARLY EVERY app — were
  // gated to heavier vendor tiers, forcing ~all apps onto the `extras`/`full`
  // bundle (~7KB gz heavier than core). Promoted into `core` (select-bundle.ts +
  // vendor/entry-core.ts), re-measured across 16 stored specs: min 141 / p50 146
  // / p95 147 / max 147 KB gz (was 146-149), variants now core/full not extras.
  // Render-parity PROVEN — a stored CRUD spec rebuilt on the new core bundle
  // renders flawlessly (VTabBar nav + VEmptyState, zero console errors). 160K =
  // the measured 147 max + ~13KB headroom for richer-than-sample content, while
  // staying far tighter than 200K. The remaining floor (~145KB) is irreducible
  // design-system infrastructure (already-variant-tiered vendor + ~57%-foundation
  // headCss + purged Tailwind), NOT bloat — a lean app shrinks app.js/content,
  // not this floor. Drops further only if a 4th sub-core vendor tier is added.
  // §7C-HR (2026-06-22) — earned-back ceiling raised 160K → 164K. The Booking
  // recipe is the RICHEST reference app and now carries three required v1
  // features the generic apps don't: the premium month calendar, the two-option
  // payment surface (chooser + honest receipts), and the customer ACCOUNT screen.
  // Measured at 160.05K gz after §7C-HR (was ~147K for generic apps); the +4K is
  // these features, not bloat creep — a non-booking app ships none of this and
  // still sits ~147K. The irreducible floor is unchanged.
  maxBytesGzipped: 164 * 1024,
  minLighthouseMobile: 85
} as const;

export const GENERATION_LATENCY_BUDGET = {
  p50Ms: 90_000,
  p95Ms: 150_000,
  // §4/§3 — lowered 600_000 → 240_000. The 10-min server ceiling was
  // DECOUPLED from the client's ~210s stream timeout: any generation past
  // ~3.5 min "succeeded" server-side long after the browser had already
  // given up and shown "errors after minutes" (the founder's #3), while the
  // worker burned compute to 8-10 min (the #4 "extremely slow" runaway —
  // observed live at 418s and 524s). 240s is the realistic ceiling for a
  // clean elite pass, the spec-pipeline + legacy fallback are each bounded
  // under it (see spec-synthesize MAX_HEAL_ATTEMPTS/SPEC_TOTAL_BUDGET_MS and
  // synthesize MAX_SYNTH_ATTEMPTS), and the client now waits this long (see
  // GenerationStream) so server and client are aligned: the user gets an app
  // or a clean retry within the window — never a multi-minute silent hang.
  // 270s (was 600s): caps the 8-10 min runaways the founder saw (418s/524s live)
  // while leaving a genuinely rich spec enough room to FINISH on the good path
  // rather than being killed into a failure (240s proved too tight for a
  // 12-item Hebrew menu). Typical generation stays ~80s.
  hardTimeoutMs: 270_000
} as const;

export const MODELS = {
  intent: 'claude-haiku-4-5',
  synthesis: 'claude-sonnet-4-6',
  /** Free-tier synthesis runs on Haiku — fast, cheap, still capable. */
  synthesisFree: 'claude-haiku-4-5'
} as const;

/** The two monetisation tiers. Anonymous traffic is always 'free'. */
export type Plan = 'free' | 'pro';

/**
 * Plan entitlements — the single source of truth for what each tier
 * gets. The gateway resolves a Plan from the signed session cookie; the
 * generation worker reads it from the X-Plan header the gateway forwards.
 */
export const PLANS = {
  free: {
    label: 'Free',
    priceUsdPerMonth: 0,
    /** Pomelas (1 Pomela = 1 generated app) per device per rolling day. */
    generationsPerDay: 3,
    /** Pomelas per device per rolling 30-day month. */
    generationsPerMonth: 10,
    /** §Bake-off gate — free/guest synthesis stays on Haiku UNTIL the deterministic
     *  supply (structural presets + few-shot exemplars + blocks-to-the-bar) is
     *  world-class. THEN a bake-off (Haiku+Supply vs Sonnet+Supply) decides if
     *  Sonnet's lift is worth ~5x. Proven: Sonnet ALONE does NOT fix "samey" —
     *  structure is deterministic, so we don't burn 5x while the supply is missing. */
    synthesisModel: MODELS.synthesisFree,
    /** Deep multi-screen planning is a Pro perk. */
    deepMode: false
  },
  pro: {
    label: 'Pro',
    priceUsdPerMonth: 19,
    generationsPerDay: 20,
    generationsPerMonth: 100,
    synthesisModel: MODELS.synthesis,
    deepMode: true
  }
} as const;

/**
 * Friendly copy shown when the device rate limit runs dry. This path serves
 * ANONYMOUS/guest traffic only (signed-in creators are governed by the unified
 * credit pool, not a per-device day/month cap — see generate-orchestrator's
 * authenticated debit branch). Keep it count-free: never surface a raw
 * builds-per-day/-month number (the Pro tier is a credit pool, not "N a day"),
 * consistent with the no-raw-count principle in apps/creator/src/screens/Upgrade.tsx.
 * Keyed by which window was hit so guests see the right refill/upgrade nudge.
 */
export function pomelaLimitMessage(plan: Plan, window: 'day' | 'month'): string {
  if (window === 'day') {
    return plan === 'pro'
      ? `You've used all of today's builds! 🍊 A fresh batch unlocks tomorrow.`
      : `You've squeezed all your builds for today! 🍊 Upgrade to Pro for a much bigger building pool.`;
  }
  return plan === 'pro'
    ? `Whoa, you're a machine — you've used your entire monthly build allowance! 🍊 It refills at the start of next month.`
    : `Whoa, you're a machine! You've used up your build allowance for the month. 🍊 Upgrade to Pro for a much bigger monthly pool.`;
}

/**
 * Curated public APIs a generated app may fetch directly from the
 * browser. Every entry MUST be keyless AND CORS-enabled (returns
 * `Access-Control-Allow-Origin: *` from a `*.pomela.app` referer), so
 * no secret can end up in client code and the request actually succeeds
 * from the page.
 *
 * This is the single source of truth for the live-data feature: the
 * generated-app CSP `connect-src`, the output validator, and the
 * synthesis system prompt are all derived from it.
 *
 * Audit reference: POMELA_NIGHT_AUDIT_REPORT.md §2.8. The previous list
 * carried four origins that silently break in production (newsapi.org
 * requires an API key off localhost, datausa.io has no public ACAO,
 * api.publicapis.org has had multi-week outages, api.quotable.io
 * ownership flux). Re-add an entry ONLY after curling from a deployed
 * pomela.app preview returns 200 with ACAO=`*`.
 */
export const LIVE_API_ALLOWLIST = [
  // §9 (2026-06-22) — re-verified live: HTTP 200 + ACAO `*` from a *.pomela.app
  // Origin. Classification lives in provider-registry.ts (the launch-safe gate);
  // this list is the CSP connect-src surface (technically-permitted). The dead
  // hosts api.frankfurter.app (301s) + api.exchangerate.host (key-gated) +
  // restcountries.com (301 → static file, no CORS) were REMOVED.
  //
  // Existing keyless live-data APIs — still live + CORS (kept for deployed apps).
  { origin: 'https://api.open-meteo.com', use: 'weather & forecasts (free tier non-commercial — prefer api.met.no for commercial)' },
  { origin: 'https://api.coingecko.com', use: 'crypto prices (demo tier non-commercial + rate-limited)' },
  { origin: 'https://api.github.com', use: 'public GitHub repo & user data' },
  { origin: 'https://pokeapi.co', use: 'Pokémon reference data' },
  { origin: 'https://api.spaceflightnewsapi.net', use: 'space news (research/demo)' },
  { origin: 'https://api.nasa.gov', use: 'NASA imagery (DEMO_KEY rate-limited)' },
  { origin: 'https://api.adviceslip.com', use: 'random advice (demo)' },
  { origin: 'https://api.chucknorris.io', use: 'demo-data jokes' },
  { origin: 'https://api.thedogapi.com', use: 'dog photos + breed reference' },
  { origin: 'https://api.thecatapi.com', use: 'cat photos + breed reference' },
  // §9 NEW — launch-safe, free + commercial-OK providers (provider-registry.ts).
  { origin: 'https://api.frankfurter.dev', use: 'currency exchange rates (free + commercial; replaces dead .app)' },
  { origin: 'https://api.met.no', use: 'weather & forecasts (MET Norway, commercial-OK; cache + identify UA)' },
  { origin: 'https://world.openfoodfacts.org', use: 'food / barcode product data (ODbL, attribution)' },
  { origin: 'https://opentdb.com', use: 'trivia / quiz questions (CC-BY-SA)' },
  { origin: 'https://en.wikipedia.org', use: 'Wikipedia article summaries (CC-BY-SA)' },
  { origin: 'https://gutendex.com', use: 'Project Gutenberg public-domain books' },
  { origin: 'https://openlibrary.org', use: 'Open Library book search (CC-BY/CC0)' },
  { origin: 'https://api.tvmaze.com', use: 'TV show data (CC-BY-SA, low rate)' },
  { origin: 'https://api.jikan.moe', use: 'anime/manga data (Jikan / MyAnimeList, rate-limited)' },
  { origin: 'https://date.nager.at', use: 'public holidays by country' },
  { origin: 'https://api.worldbank.org', use: 'World Bank open development data' },
  // §ARSENAL (2026-07-29) — verified per the rule above: 200 + ACAO `*` with a pomela.page Origin.
  { origin: 'https://www.wikidata.org', use: 'Wikidata entities (CC0; use /wiki/Special:EntityData/<QID>.json — the /w/rest.php/v1 shape 400s)' },
  // Technically permitted so a creator CAN opt in, but NOT launch-safe: the OSM policy caps this
  // at 1 req/s and asks heavy users to self-host, so it must be debounced + cached, never wired
  // to a keystroke. provider-registry.ts holds that gate (launchSafe: false).
  { origin: 'https://nominatim.openstreetmap.org', use: 'OSM geocoding (ODbL, attribution required, HARD 1 req/s — opt-in only)' },
  // §PHASE8-CONNECTORS (2026-08-24) — added under the SAME rule as every entry above: measured
  // live, 200 + ACAO `*` with an Origin of https://demo.pomela.page, before being written here.
  //
  // Two candidates were measured and REFUSED rather than added, which is the point of the rule:
  //   • https://www.ecb.europa.eu   200, but NO access-control-allow-origin — a browser fetch from
  //     an app origin is blocked, so listing it would advertise a connector that cannot connect.
  //   • https://overpass-api.de     504 Gateway Timeout on a trivial bbox query. The shared instance
  //     is exactly as loaded as OSM's own policy warns; an origin that times out is not buildable.
  { origin: 'https://earthquake.usgs.gov', use: 'USGS earthquake feeds (US Government public domain, commercial OK)' },
  { origin: 'https://collectionapi.metmuseum.org', use: 'Met Museum open-access collection (CC0 for open-access objects)' },
  // §PHASE8-BAR (2026-08-25) — each measured live under the same rule: 200 plus
  // access-control-allow-origin, from an Origin of https://demo.pomela.page. Four candidates were
  // measured and REFUSED rather than added, which is what makes the rule worth having:
  //   catalog.data.gov  404 on the CKAN action API   · gutendex.com   301 then no response
  //   api.gsa.gov       403, key required            · transit.land   401, key required
  //
  // api.met.no and world.openfoodfacts.org, date.nager.at and opentdb.com were ALREADY on this list
  // from §9 — they gain catalog entries here rather than new origins.
  { origin: 'https://air-quality-api.open-meteo.com', use: 'Open-Meteo air quality (free tier non-commercial — same terms as the forecast API)' },
  { origin: 'https://api.weather.gov', use: 'US National Weather Service forecasts and alerts (US Government public domain)' },
  { origin: 'https://www.federalregister.gov', use: 'US Federal Register rules and notices (US Government public domain)' },
  { origin: 'https://api-v3.mbta.com', use: 'MBTA transit — routes, schedules, predictions (MassDOT developer agreement; review terms)' },
  { origin: 'https://api.tfl.gov.uk', use: 'Transport for London status (attribution: "Powered by TfL Open Data")' },
  { origin: 'https://api.artic.edu', use: 'Art Institute of Chicago collection (CC0 for public-domain works ONLY — check is_public_domain per record)' }
] as const;

/** Just the origins — for the CSP connect-src and the output validator. */
export const LIVE_API_ORIGINS: readonly string[] = LIVE_API_ALLOWLIST.map((a) => a.origin);

/**
 * §PHASE8-PROXY-ALLOWLIST — origins the SERVER may fetch on an app's behalf, which is a strictly
 * different question from the one `LIVE_API_ALLOWLIST` answers.
 *
 * That list is the CSP connect-src surface: what a generated app's BROWSER may call directly. An
 * origin only belongs there if it answers with `access-control-allow-origin`, because without it the
 * browser refuses the response and the app shows an error no matter how healthy the provider is.
 *
 * The connector runtime does not have that constraint. It fetches from a Worker, where CORS does not
 * apply at all, and it adds the things a direct browser fetch cannot have: the API key stays
 * server-side, the response is cached to the connector's declared TTL, the origin is pinned and
 * redirects are refused. Gating that path on the CSP list meant a source was rejected as
 * `reference_only` for failing a test it was never taking — the European Central Bank's daily
 * reference rates answer 200 with no CORS header, and were unreachable through a proxy that has no
 * use for one.
 *
 * SAFE BY CONSTRUCTION: this is a fixed table of vetted origins, never a URL from a request. Nothing
 * a user types can widen it, so the proxy cannot be turned into an SSRF gadget by adding entries here.
 * Every entry is a superset member — direct-fetchable origins remain proxy-fetchable too.
 */
export const PROXY_ONLY_ORIGINS = [
  {
    origin: 'https://www.ecb.europa.eu',
    use: 'ECB euro foreign-exchange reference rates (daily XML)',
    // Measured 2026-08-24: 200 text/xml, and NO access-control-allow-origin. Direct browser fetch is
    // impossible; the server-side fetch is unaffected.
    whyProxyOnly: 'provider sends no CORS header, so only a server-side fetch can read it',
  },
] as const;

/** Origins the connector proxy may fetch: everything the browser may call, plus the server-only set. */
export const PROXY_ORIGIN_ALLOWLIST: readonly string[] = [
  ...LIVE_API_ORIGINS,
  ...PROXY_ONLY_ORIGINS.map((p) => p.origin),
];

/**
 * Common-API registry (F11) — ready-to-use recipes for the keyless live APIs.
 *
 * The LLM was previously handed only the bare origins (`https://api.open-meteo.com`)
 * and had to GUESS each endpoint's path + response accessor — which it often got
 * wrong, and a dead live fetch reads to the user as a broken app. Each recipe
 * below is curl-verified (HTTP 200 + permissive CORS from a *.pomela.app Origin)
 * and hands the model a working { origin, path, accessor } it can drop straight
 * into a `spec.data` entry of kind:"live", PLUS the response shape so it binds
 * the right fields instead of inventing them.
 *
 * Maintenance: re-verify a recipe (curl from a pomela.app Origin → 200 + ACAO)
 * before trusting it — third-party APIs drift. §9 (2026-06-22): the old dead
 * currency hosts were replaced by api.frankfurter.dev (verified 200 + ACAO `*`),
 * and the restcountries `countries` recipe was REMOVED (restcountries.com now
 * 301-redirects to a static file with no CORS). New launch-safe recipes added.
 */
export interface LiveApiRecipe {
  /** Short stable id (also the suggested dataKey). */
  id: string;
  /** MUST be one of LIVE_API_ALLOWLIST — the CSP connect-src enforces it. */
  origin: string;
  /** Example path incl. query string. The model edits params (coords, ids…). */
  path: string;
  /** JSON-path accessor into the response. '' = the response root. */
  accessor: string;
  /** One line on what it's for. */
  use: string;
  /** The shape AT `accessor`, so the model binds real fields not guesses. */
  returns: string;
}

export const LIVE_API_RECIPES: readonly LiveApiRecipe[] = [
  {
    id: 'weather',
    origin: 'https://api.open-meteo.com',
    path: '/v1/forecast?latitude=40.71&longitude=-74.01&current=temperature_2m,weather_code,wind_speed_10m',
    accessor: 'current',
    use: 'Current weather by lat/lng (no key). Add &hourly=… or &daily=… for forecasts.',
    returns: '{ time:string, temperature_2m:number, weather_code:number, wind_speed_10m:number }'
  },
  {
    id: 'crypto_prices',
    origin: 'https://api.coingecko.com',
    path: '/api/v3/simple/price?ids=bitcoin,ethereum,solana&vs_currencies=usd&include_24hr_change=true',
    accessor: '',
    use: 'Spot price + 24h change for named coins (root object keyed by coin id).',
    returns: '{ bitcoin:{ usd:number, usd_24h_change:number }, ethereum:{…}, … }'
  },
  {
    id: 'crypto_markets',
    origin: 'https://api.coingecko.com',
    path: '/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=10&page=1',
    accessor: '',
    use: 'Top coins by market cap — an ARRAY, ideal for a DataTable/List.',
    returns: '[{ id, symbol, name, image, current_price:number, market_cap:number, price_change_percentage_24h:number }]'
  },
  {
    id: 'github_repo',
    origin: 'https://api.github.com',
    path: '/repos/facebook/react',
    accessor: '',
    use: 'Public repo stats. Swap owner/repo; use /users/<login> for a profile.',
    returns: '{ full_name:string, description:string, stargazers_count:number, forks_count:number, open_issues_count:number, language:string }'
  },
  // §9 — launch-safe additions (each curl-verified 2026-06-22: 200 + ACAO `*`).
  {
    id: 'currency',
    origin: 'https://api.frankfurter.dev',
    path: '/v1/latest?base=USD&symbols=EUR,GBP,JPY',
    accessor: 'rates',
    use: 'Daily FX rates (ECB; free + commercial). Edit base/symbols. /v1/2024-01-02..?base= for history.',
    returns: '{ EUR:number, GBP:number, JPY:number }'
  },
  {
    id: 'weather_metno',
    origin: 'https://api.met.no',
    path: '/weatherapi/locationforecast/2.0/compact?lat=40.71&lon=-74.01',
    accessor: 'properties.timeseries',
    use: 'MET Norway forecast (commercial-OK; cache hard, send a real User-Agent). ARRAY of timesteps.',
    returns: '[{ time:string, data:{ instant:{ details:{ air_temperature:number, wind_speed:number } } } }]'
  },
  {
    id: 'trivia',
    origin: 'https://opentdb.com',
    path: '/api.php?amount=10&type=multiple',
    accessor: 'results',
    use: 'Open Trivia DB quiz questions — an ARRAY. Add &category=&difficulty=. HTML-decode the text.',
    returns: '[{ category:string, difficulty:string, question:string, correct_answer:string, incorrect_answers:[string] }]'
  },
  {
    id: 'food_product',
    origin: 'https://world.openfoodfacts.org',
    path: '/api/v2/product/737628064502.json?fields=product_name,brands,nutriscore_grade,image_url',
    accessor: 'product',
    use: 'Open Food Facts product by barcode (ODbL — attribute). Swap the barcode in the path.',
    returns: '{ product_name:string, brands:string, nutriscore_grade:string, image_url:string }'
  },
  {
    id: 'wiki_summary',
    origin: 'https://en.wikipedia.org',
    path: '/api/rest_v1/page/summary/Cat',
    accessor: '',
    use: 'Wikipedia article summary (CC-BY-SA — attribute). Swap the title in the path (URL-encode).',
    returns: '{ title:string, extract:string, thumbnail:{ source:string }, content_urls:{ desktop:{ page:string } } }'
  },
  {
    id: 'tv_search',
    origin: 'https://api.tvmaze.com',
    path: '/search/shows?q=girls',
    accessor: '',
    use: 'TVmaze show search — an ARRAY of { show }. Use /shows/<id> for one show.',
    returns: '[{ show:{ id:number, name:string, genres:[string], status:string, image:{ medium:string }, summary:string } }]'
  },
  {
    id: 'anime_search',
    origin: 'https://api.jikan.moe',
    path: '/v4/anime?q=naruto&limit=10',
    accessor: 'data',
    use: 'Jikan (MyAnimeList) anime search — rate-limited (~3/s). ARRAY at .data.',
    returns: '[{ mal_id:number, title:string, score:number, episodes:number, images:{ jpg:{ image_url:string } } }]'
  },
  {
    id: 'holidays',
    origin: 'https://date.nager.at',
    path: '/api/v3/PublicHolidays/2026/US',
    accessor: '',
    use: 'Public holidays for a year + ISO country — an ARRAY. Swap the year/country in the path.',
    returns: '[{ date:string, localName:string, name:string, countryCode:string }]'
  },
  {
    id: 'worldbank_indicator',
    origin: 'https://api.worldbank.org',
    path: '/v2/country/US/indicator/SP.POP.TOTL?format=json&per_page=5',
    accessor: '1',
    use: 'World Bank indicator series (response is [meta, data]; accessor "1" is the data ARRAY).',
    returns: '[{ country:{ value:string }, date:string, value:number }]'
  },
  {
    id: 'books',
    origin: 'https://gutendex.com',
    path: '/books/?search=tolstoy',
    accessor: 'results',
    use: 'Project Gutenberg public-domain books (note the trailing slash on /books/). ARRAY at .results.',
    returns: '[{ id:number, title:string, authors:[{ name:string }], formats:{ "text/html":string } }]'
  }
];

export const A2HS_COACH = {
  minEngagementSeconds: 8,
  dismissalCooldownDays: 7,
  storageKey: 'vs:install-coach'
} as const;

export const STORAGE_SYNC = {
  debounceMs: 800,
  maxBytesPerKey: 5 * 1024 * 1024
} as const;

// Domain-derived constants. The apex domain lives in brand.ts (the
// single source of truth) so a name change is one env-var edit.
export const ROOT_DOMAIN = BRAND.domainApex;
export const API_BASE = BRAND.apiOrigin;
export const APPS_BASE = `https://og.${BRAND.domainApex}`;
